/**
 * Validation for sign in, registration and password reset.
 * Pure functions, shared by the pages and the API routes.
 */

import type { AccountFor, Gender, RegistrationField } from '@/types'

import { normalizePhone } from '@/lib/booking-validation'
import { parseIsoDate } from '@/lib/dates'

export const PASSWORD_MIN = 8
export const PASSWORD_MAX = 128
export const NAME_MAX = 100
export const ADDRESS_MIN = 10
export const ADDRESS_MAX = 300

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const normalizeEmail = (raw: string): string => raw.trim().toLowerCase()

export const nikDigits = (raw: string): string => raw.replace(/\D/g, '')

export const validateEmail = (raw: string): string | null => {
    const email = raw.trim()

    if (!email) return 'Email wajib diisi.'
    if (email.length > 254 || !EMAIL_PATTERN.test(email)) return 'Masukkan email yang valid, misalnya nama@contoh.com.'

    return null
}

export const validateLoginPassword = (raw: string): string | null => {
    return raw ? null : 'Kata sandi wajib diisi.'
}

export const validateNewPassword = (raw: string): string | null => {
    if (!raw) return 'Kata sandi wajib diisi.'
    if (raw.length > PASSWORD_MAX) return `Kata sandi maksimal ${PASSWORD_MAX} karakter.`
    if (raw.length < PASSWORD_MIN || !/[A-Za-z]/.test(raw) || !/\d/.test(raw)) return 'Gunakan minimal 8 karakter dengan kombinasi huruf dan angka.'

    return null
}

export const validatePasswordConfirmation = (password: string, confirmation: string): string | null => {
    if (!confirmation) return 'Ulangi kata sandi wajib diisi.'
    if (password !== confirmation) return 'Konfirmasi harus sama dengan kata sandi.'

    return null
}

export const validateFullName = (raw: string): string | null => {
    const name = raw.replace(/\s+/g, ' ').trim()

    if (!name) return 'Nama lengkap wajib diisi.'
    if (name.length < 3) return 'Tulis nama lengkap sesuai KTP.'
    if (name.length > NAME_MAX) return `Nama lengkap maksimal ${NAME_MAX} karakter.`
    if (!/[a-zA-ZÀ-ɏ]/.test(name)) return 'Nama hanya boleh berisi huruf.'

    return null
}

export const validateNik = (raw: string): string | null => {
    const digits = nikDigits(raw)

    if (!raw.trim()) return 'NIK wajib diisi.'
    if (/[^\d\s.-]/.test(raw)) return 'NIK hanya berisi angka.'
    if (digits.length !== 16) return `NIK harus terdiri dari 16 digit (terisi ${digits.length} digit).`

    return null
}

/** Full years between two YYYY-MM-DD dates */
export const ageOn = (birth: string, today: string): number => {
    const [birthYear, birthMonth, birthDay] = birth.split('-').map(Number)
    const [year, month, day] = today.split('-').map(Number)
    const beforeBirthday = month < birthMonth || (month === birthMonth && day < birthDay)

    return year - birthYear - (beforeBirthday ? 1 : 0)
}

/** today is the current WIB date as YYYY-MM-DD */
export const validateBirthDate = (iso: string, today: string): string | null => {
    if (!iso) return 'Tanggal lahir wajib diisi.'
    if (!parseIsoDate(iso)) return 'Masukkan tanggal lahir yang valid.'
    if (iso > today) return 'Tanggal lahir tidak boleh melewati hari ini.'

    const age = ageOn(iso, today)

    if (age < 18) return 'Pemilik akun harus berusia 18 tahun ke atas.'
    if (age > 120) return 'Periksa kembali tahun lahir Anda.'

    return null
}

export const validateGender = (value: string): string | null => {
    return value === 'laki-laki' || value === 'perempuan' ? null : 'Pilih jenis kelamin.'
}

export const validateAddress = (raw: string): string | null => {
    const address = raw.trim()

    if (!address) return 'Alamat domisili wajib diisi.'
    if (address.length < ADDRESS_MIN) return 'Tulis alamat lengkap: jalan, RT/RW, kelurahan, dan kecamatan.'
    if (address.length > ADDRESS_MAX) return `Alamat maksimal ${ADDRESS_MAX} karakter.`

    return null
}

export const validatePhoneNumber = (raw: string): string | null => {
    if (!raw.trim()) return 'Nomor telepon wajib diisi.'
    if (!normalizePhone(raw)) return 'Gunakan nomor ponsel Indonesia yang aktif, misalnya 0812 3456 7890.'

    return null
}

export type PersonalData = {
    namaLengkap: string
    nik: string
    tanggalLahir: string
    jenisKelamin: string
    alamat: string
}

export type AccountData = {
    email: string
    telepon: string
    kataSandi: string
    ulangiKataSandi: string
    persetujuan: boolean
}

export type FieldErrors = Partial<Record<RegistrationField, string>>

export const validatePersonalData = (data: PersonalData, today: string): FieldErrors => {
    const errors: FieldErrors = {}
    const checks: Array<[RegistrationField, string | null]> = [
        ['namaLengkap', validateFullName(data.namaLengkap)],
        ['nik', validateNik(data.nik)],
        ['tanggalLahir', validateBirthDate(data.tanggalLahir, today)],
        ['jenisKelamin', validateGender(data.jenisKelamin)],
        ['alamat', validateAddress(data.alamat)]
    ]

    for (const [field, message] of checks) {
        if (message) errors[field] = message
    }

    return errors
}

export const validateAccountData = (data: AccountData): FieldErrors => {
    const errors: FieldErrors = {}
    const checks: Array<[RegistrationField, string | null]> = [
        ['email', validateEmail(data.email)],
        ['telepon', validatePhoneNumber(data.telepon)],
        ['kataSandi', validateNewPassword(data.kataSandi)],
        ['ulangiKataSandi', validatePasswordConfirmation(data.kataSandi, data.ulangiKataSandi)],
        ['persetujuan', data.persetujuan ? null : 'Setujui pernyataan ini untuk membuat akun.']
    ]

    for (const [field, message] of checks) {
        if (message) errors[field] = message
    }

    return errors
}

export type ValidRegistration = {
    untuk: AccountFor
    namaLengkap: string
    nik: string
    tanggalLahir: string
    jenisKelamin: Gender
    alamat: string
    email: string
    telepon: string
    kataSandi: string
}

export type RegistrationResult =
    | { ok: true, value: ValidRegistration }
    | { ok: false, step: 1 | 2 | 3, fields: FieldErrors }

const asString = (value: unknown): string => (typeof value === 'string' ? value : '')

export const validateRegistration = (body: unknown, today: string): RegistrationResult => {
    const input = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
    const untuk = asString(input.untuk)

    if (untuk !== 'diri-sendiri' && untuk !== 'anak') {
        return { ok: false, step: 1, fields: { untuk: 'Pilih untuk siapa akun ini dibuat.' } }
    }

    const personal: PersonalData = {
        namaLengkap: asString(input.namaLengkap),
        nik: asString(input.nik),
        tanggalLahir: asString(input.tanggalLahir),
        jenisKelamin: asString(input.jenisKelamin),
        alamat: asString(input.alamat)
    }

    const personalErrors = validatePersonalData(personal, today)
    if (Object.keys(personalErrors).length > 0) return { ok: false, step: 2, fields: personalErrors }

    const account: AccountData = {
        email: asString(input.email),
        telepon: asString(input.telepon),
        kataSandi: asString(input.kataSandi),
        ulangiKataSandi: asString(input.ulangiKataSandi),
        persetujuan: input.persetujuan === true
    }

    const accountErrors = validateAccountData(account)
    if (Object.keys(accountErrors).length > 0) return { ok: false, step: 3, fields: accountErrors }

    return {
        ok: true,
        value: {
            untuk,
            namaLengkap: personal.namaLengkap.replace(/\s+/g, ' ').trim(),
            nik: nikDigits(personal.nik),
            tanggalLahir: personal.tanggalLahir,
            jenisKelamin: personal.jenisKelamin as Gender,
            alamat: personal.alamat.trim(),
            email: normalizeEmail(account.email),
            telepon: normalizePhone(account.telepon) ?? account.telepon,
            kataSandi: account.kataSandi
        }
    }
}
